import { useDispatch } from "react-redux";
import { authActions } from "../../store/authSlice";
import useStorageManager from "./useStorageManager";

type AccessTokens = {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user_id: string;
};

const useTokens = () => {
  const {
    getCookie,
    setCookie,
    deleteCookie,
    saveToLocalStorage,
    getFromLocalStorage,
  } = useStorageManager();
  const dispatch = useDispatch();
  /**
   * Retrieves the access token from cookies or localStorage.
   */
  const getToken = () => {
    return getCookie("token") || getFromLocalStorage("token") || "";
  };

  /**
   * Retrieves the refresh token from cookies or localStorage.
   */
  const getRefreshToken = () => {
    return (
      getCookie("refresh_token") || getFromLocalStorage("refresh_token") || ""
    );
  };

  /**
   * Sets both access and refresh tokens in cookies and localStorage.
   * @param {Object} data - The token data containing `access_token`, `refresh_token`, and `expires_in`.
   */
  const setAccessTokens = (data: AccessTokens) => {
    const expiresAt: number = calculateRefreshTime(data.expires_in);

    // Set cookies
    setCookie("token", data.access_token);
    setCookie("refresh_token", data.refresh_token);

    // Set localStorage
    saveToLocalStorage("token", data.access_token);
    saveToLocalStorage("refresh_token", data.refresh_token);
    saveToLocalStorage("expires_in", String(expiresAt));
    dispatch(authActions.login({
      token: data.access_token,
      userId: data.user_id,
    }));
  };

  const calculateRefreshTime = (expiresIn: number) => {
    const timer: number = expiresIn * 1000 - expiresIn * 1000 * 0.05;
    const now = new Date().getTime();
    const expiresAt = now + timer;
    return expiresAt;
  };

  /**
   * Clears all tokens from cookies and localStorage.
   */
  const clearTokens = (timeOut: boolean = false) => {
    sessionStorage.clear();

    // Clear all localStorage except theme-related keys
    Object.keys(localStorage).forEach((key) => {
      if (!key.startsWith("theme")) {
        localStorage.removeItem(key);
      }
    });

    deleteCookie("token");
    deleteCookie("refresh_token");
    deleteCookie("encryptionAllowed");


    if (timeOut) {
      setCookie("timeout", "true");
    }
  };

  return {
    getToken,
    getRefreshToken,
    setAccessTokens,
    clearTokens,
    calculateRefreshTime,
  };
};

export default useTokens;
